import { useQuery } from '@tanstack/react-query'

import { isDevToolsEnabled, setApiBaseUrlOverride } from '@/config/api-base-url'
import { env } from '@/config/env'
import { tokenService } from '@/domain/auth/token.service'
import { queryClient } from '@/lib/query-client'

import { fetchBrandUsers, fetchReviewApps } from './admin-api'

// Unified password for the seeded brand test users on review/staging apps.
export const TEST_USER_PASSWORD = 'Test1234'

// Queries only run when the dev tools are enabled (non-production) and a key was
// baked into the build; otherwise they stay idle and the UI shows a hint.
const canQuery = isDevToolsEnabled && !!env.VITE_REVIEW_APPS_API_KEY

export function useReviewApps() {
  return useQuery({
    queryKey: ['admin', 'review-apps'],
    queryFn: fetchReviewApps,
    enabled: canQuery,
    retry: 1,
    staleTime: 1000 * 60 * 5,
  })
}

export function useBrandUsers(search: string, baseUrl: string) {
  // Users are queried against whichever environment is currently active, so the
  // active base URL is part of the key — changing it refetches automatically.
  return useQuery({
    queryKey: ['admin', 'brand-users', baseUrl, search],
    queryFn: () => fetchBrandUsers(search, baseUrl),
    enabled: canQuery,
    staleTime: 1000 * 30,
  })
}

// Point the whole app at a review-app environment: persist the override, drop the
// current session, and clear cached server state so nothing from the previous
// environment leaks across. No reload — callers keep their UI (e.g. the dev tools
// modal) and continue to the next step; any authed view resets on next navigation.
export function switchEnvironment(apiUrl: string): void {
  setApiBaseUrlOverride(apiUrl)
  tokenService.clearTokens()
  queryClient.clear()
}

// Sign in as a test user: clear the current session and land on login with the
// email prefilled (a password is still required).
export function loginAsTestUser(email: string): void {
  tokenService.clearTokens()
  if (typeof window !== 'undefined') {
    window.location.assign(`/login?email=${encodeURIComponent(email)}`)
  }
}
